import i18nConfig from '../i18n';
import { getConfig } from './config';
import graphQLClient from './graphql/graphql-client';
import {
  GET_BLOG,
  GET_BLOGLIST,
  GET_NEWS,
  GET_NEWSLIST,
  GET_NEWS_BY_ID,
  GET_PAGE_URLS_QUERY,
  GET_PAGE_BY_ID_QUERY,
  GET_PAGE_BY_URL_QUERY,
  GET_SETTINGS,
  GET_NEWS_SLUGS_QUERY,
  GET_CONNECTED_LOCALIZED_PAGES,
  GET_CONNECTED_LOCALIZED_NEWS,
} from './graphql/queries';
import {
  parseBlog,
  parseBlogList,
  parseNews,
  parseNewsList,
  parseMenu,
  parsePage,
  parseSettings,
} from './parse';
import { Blog, News, Menu, Page, MetaPagination } from './types';

const NEWS_PAGE_SLUG = '/news/';

const parseLocale = (l?: string): string => {
  if (!l) return '';
  return l.replace('-us', '-US');
};

export async function getPageUrls(locale: string): Promise<string[]> {
  let allPages: string[] = [];
  let page = 1;
  const pageSize = 100;

  while (true) {
    const {
      pages: { data, meta },
    } = await graphQLClient.request(GET_PAGE_URLS_QUERY, {
      locale: parseLocale(locale),
      page,
      pageSize,
    });

    allPages = allPages.concat(data.map((page: any) => page.attributes.url));
    if (page >= meta.pagination.pageCount) {
      break;
    }

    page++;
  }

  return allPages;
}

export async function getPageByUrl(
  url: string,
  locale?: string,
  isPreview: boolean = false,
) {
  const { pages } = await graphQLClient.request(GET_PAGE_BY_URL_QUERY, {
    url,
    locale: parseLocale(locale),
    publicationState: isPreview ? 'PREVIEW' : 'LIVE',
  });

  if (pages.data.length === 0) {
    return null;
  }

  return await parsePage(pages.data[0]);
}

export async function getPageById(id: string): Promise<Page | null> {
  if (!id) {
    return null;
  }

  const { page } = await graphQLClient.request(GET_PAGE_BY_ID_QUERY, {
    id,
  });

  if (!page) {
    return null;
  }

  return await parsePage(page.data);
}

type ConnectedEntity = {
  locale: string;
  url: string;
};

type ConnectedEntities = ConnectedEntity[] | null;

const connectedNews = async (
  url: string,
  locale: string,
  targetLocale: string,
): Promise<ConnectedEntities> => {
  const slug = url.substring(NEWS_PAGE_SLUG.length);

  const { newsList } = await graphQLClient.request(
    GET_CONNECTED_LOCALIZED_NEWS,
    { locale, publicationState: 'LIVE', slug },
  );

  if (!newsList.data || newsList.data.length === 0) return null;

  const newsLocales = [
    { locale, url },
    ...newsList.data[0].attributes.localizations.data.map(
      (l: { attributes: { locale: string; slug: string } }) => ({
        locale: l.attributes.locale,
        url: `${NEWS_PAGE_SLUG}${l.attributes.slug}`,
      }),
    ),
  ];

  // At the moment we show the en-news for all locales. Return `newsLocales` if you want to show localized news.
  // Here we return the the url of en news for the target locale.

  const newsEn = newsLocales.find((l) => l.locale === 'en');
  if (!newsEn) return null;

  return newsLocales.map((l) => ({
    locale: targetLocale,
    url: newsEn.url,
  }));
};

const connectedPages = async (
  url: string,
  locale: string,
): Promise<ConnectedEntities> => {
  const { pages } = await graphQLClient.request(GET_CONNECTED_LOCALIZED_PAGES, {
    locale,
    publicationState: 'LIVE',
    url,
  });

  if (!pages.data || pages.data.length === 0) return null;

  return [
    { locale, url },
    ...pages.data[0].attributes.localizations.data.map(
      (l: { attributes: { locale: string; url: string } }) => ({
        locale: l.attributes.locale,
        url: l.attributes.url,
      }),
    ),
  ];
};

/**
 * Finds a page or news entity by `url` and `locale`, then returns the URL of the entity matching the `targetLocale`.
 */
export async function getConnectedEntitiesWithLocale(
  url: string,
  locale: string,
  targetLocale: string,
) {
  const _locale = parseLocale(locale);

  const entities: ConnectedEntities = url.startsWith(NEWS_PAGE_SLUG)
    ? await connectedNews(url, _locale, targetLocale)
    : await connectedPages(url, _locale);

  if (!entities) return null;

  return entities.find((l) => l.locale === targetLocale)?.url || null;
}

export async function getMenu(
  name: 'main' | 'footer' | 'footer-secondary',
  locale?: string,
): Promise<Menu> {
  if (!locale) {
    locale = i18nConfig.defaultLocale;
  }

  const response = await fetch(
    `${getConfig().MEDIA_URL}/api/menus/?filters[slug][$eq]=${name}${
      locale !== i18nConfig.defaultLocale ? `-${parseLocale(locale)}` : ''
    }&nested&populate=*`,
  );

  const data = await response.json();

  return parseMenu(data);
}

export async function getSettings(locale?: string): Promise<any> {
  const { setting } = await graphQLClient.request(GET_SETTINGS, {
    locale: parseLocale(locale),
  });

  if (!setting?.data) {
    return null;
  }

  return parseSettings(setting.data);
}

export async function getNewsSlugs(): Promise<string[]> {
  let allPages: string[] = [];
  let page = 1;
  const pageSize = 100;

  while (true) {
    const {
      newsList: { data, meta },
    } = await graphQLClient.request(GET_NEWS_SLUGS_QUERY, {
      page,
      pageSize,
    });

    allPages = allPages.concat(
      data.map((page: any) => `/news/${page.attributes.slug}`),
    );
    if (page >= meta.pagination.pageCount) {
      break;
    }

    page++;
  }

  return allPages;
}

export async function getNewsList(
  locale?: string,
  page: number = 1,
  pageSize: number = 10,
): Promise<{
  news: News[];
  meta: {
    pagination: MetaPagination;
  };
}> {
  const { newsList } = await graphQLClient.request(GET_NEWSLIST, {
    locale,
    page,
    pageSize,
  });

  if (newsList.data.length === 0) {
    return { news: [], meta: newsList.meta };
  }

  return { news: parseNewsList(newsList.data), meta: newsList.meta };
}

export async function getNews(
  slug: string[],
  isPreview: boolean,
  locale?: string,
): Promise<News | null> {
  const { newsList } = await graphQLClient.request(GET_NEWS, {
    slug,
    locale: parseLocale(locale),
    publicationState: isPreview ? 'PREVIEW' : 'LIVE',
  });

  if (newsList.data.length == 0) {
    return null;
  }

  return parseNews(newsList.data[0]);
}

export async function getBlogList(
  locale?: string,
  page: number = 1,
  pageSize: number = 10,
): Promise<{
  blogs: Blog[];
  meta: {
    pagination: MetaPagination;
  };
}> {
  const { blogs } = await graphQLClient.request(GET_BLOGLIST, {
    locale: parseLocale(locale),
    page,
    pageSize,
  });

  if (blogs.data.length === 0) {
    return { blogs: [], meta: blogs.meta };
  }

  return { blogs: parseBlogList(blogs.data), meta: blogs.meta };
}

export async function getBlog(
  slug: string[],
  isPreview: boolean,
  locale?: string,
): Promise<Blog | null> {
  const { blogs } = await graphQLClient.request(GET_BLOG, {
    slug,
    locale: parseLocale(locale),
    publicationState: isPreview ? 'PREVIEW' : 'LIVE',
  });

  if (blogs.data.length == 0) {
    return null;
  }

  return parseBlog(blogs.data[0]);
}

export async function getNewsById(
  id: string,
  locale?: string,
): Promise<News | null> {
  const { news } = await graphQLClient.request(GET_NEWS_BY_ID, { id });

  if (!news) {
    return null;
  }

  return await parseNews(news.data);
}
